import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import MuiBox from '@mui/material/Box';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { ReactNode } from 'react';
import { fontWeights } from '../../theme';

// Height of the header row (MUI's `disableGutters` summary, which does not grow when expanded).
// The actions float over that row, so they need it as a number, not as a class.
const HEADER_HEIGHT = 48;

export interface DisclosureProps {
  // Id of the body region — also the anchor of the header's `aria-controls`. Must be unique on
  // the page, since two disclosures side by side is the ordinary case.
  id: string;
  // Header text. Prose in sentence case, NOT a button label: this is why the panel is an
  // Accordion and not a `Button variant="text"`, whose theme puts every label in uppercase.
  title: string;
  expanded: boolean;
  onToggle: () => void;
  // Sits right after the title, for a state the header must carry (e.g. a status Chip).
  badge?: ReactNode;
  // Controls acting on the open panel, at the far end of the header row. They are laid OVER the
  // header, never inside it — see the note on the layout below.
  actions?: ReactNode;
  // Draws the panel as an unsaved draft: a dashed warning outline instead of the plain hairline.
  // Deliberately a shape+colour change and not colour alone — the draft state must survive a
  // monochrome print and a colour-blind reader, and whatever it marks is also said in words.
  draft?: boolean;
  // Blocks the toggle only: the body stays mounted (see `unmountOnExit` below) and `actions` keep
  // their own `disabled`.
  disabled?: boolean;
  children: ReactNode;
}

// A collapsible panel: header always visible, body revealed on demand. Controlled — the caller owns
// `expanded`, which is what lets a group of them behave as one exclusive set (only one open) without
// this component knowing the group exists.
//
// **Actions are overlaid, not nested.** MUI's AccordionSummary is a real `<button>`, so a control
// placed inside it would be a button within a button — invalid HTML, and a focusable descendant of
// something ARIA says must have none. Instead the actions are absolutely positioned over the header
// row, above it in the stacking order: the toggle keeps the whole row as its hit area, the actions
// take their own slice of it, and neither needs to stop the other's events.
//
// The body UNMOUNTS while collapsed (`unmountOnExit`) rather than merely hiding: a folded panel
// should not keep its content in the DOM where a find-in-page or a screen reader can reach it, and
// callers rely on collapsing to discard whatever transient state the body was holding.
//
// `disabled` deliberately does NOT extend that rule: unmounting is for a panel being put away, and a
// disabled one is mid-decision — usually a write in flight over the content on screen.
//
// The chevron is moved to the LEAD of the header (`row-reverse`) instead of MUI's trailing default,
// so the affordance sits where the eye starts the line and the header's far end is free for the
// actions. MUI still owns its rotation, so collapsed ▾ / expanded ▴ costs no state here.
export function Disclosure({
  id,
  title,
  expanded,
  onToggle,
  badge,
  actions,
  draft = false,
  disabled = false,
  children,
}: DisclosureProps) {
  return (
    <MuiAccordion
      expanded={expanded}
      onChange={onToggle}
      disableGutters
      elevation={0}
      square={false}
      // Omitted rather than passed as `false`: `exactOptionalPropertyTypes` rejects an explicit
      // undefined, and this keeps the default path byte-identical to before the prop existed.
      {...(disabled ? { disabled } : {})}
      slotProps={{ transition: { unmountOnExit: true } }}
      sx={{
        position: 'relative',
        border: '1px',
        borderStyle: draft ? 'dashed' : 'solid',
        borderColor: draft ? 'warning.main' : 'divider',
        borderRadius: 2,
        // MUI rounds a STACK: `:first-of-type` keeps the top pair and `:last-of-type` the bottom
        // pair, everything between them square, so joined accordions read as one block. These are
        // spaced cards, so each has to close on all four corners. It takes an override of the same
        // shape and not just a plain `borderRadius`: MUI's rules are compound selectors and beat a
        // bare class on specificity no matter which stylesheet lands last.
        '&:first-of-type, &:last-of-type': { borderRadius: 2 },
        // A collapsed panel recedes to the section's fill and the open one lifts to paper — the
        // only cue that survives when the header text of both reads the same.
        backgroundColor: expanded ? 'background.paper' : 'grey.50',
        // MUI draws a hairline above every accordion to separate stacked ones; these are spaced
        // cards, so the rule is a stray line across the top.
        '&::before': { display: 'none' },
      }}
    >
      <MuiAccordionSummary
        id={`${id}-toggle`}
        aria-controls={id}
        expandIcon={<ExpandMoreIcon fontSize="small" />}
        sx={{
          flexDirection: 'row-reverse',
          gap: 1,
          // The icon slot keeps MUI's trailing margin, which in row-reverse strands the chevron
          // away from the title it belongs to.
          '& .MuiAccordionSummary-expandIconWrapper': { marginRight: 0 },
          '& .MuiAccordionSummary-content': {
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            // Keeps a long title from running under the overlaid actions.
            paddingRight: actions ? 9 : 0,
          },
        }}
      >
        {/* Bold: the header names the panel, so it has to hold its own against the content it
            reveals — and against a sibling panel's header when several are stacked. In its own
            span rather than on the summary, so a badge beside it keeps the body weight. */}
        <MuiBox component="span" sx={{ fontWeight: fontWeights.bold }}>
          {title}
        </MuiBox>
        {badge}
      </MuiAccordionSummary>
      {actions !== undefined && (
        <MuiBox
          sx={{
            position: 'absolute',
            top: 0,
            right: 8,
            height: HEADER_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            // Over the toggle, so a click on an action never reaches it.
            zIndex: 1,
          }}
        >
          {actions}
        </MuiBox>
      )}
      <MuiAccordionDetails>{children}</MuiAccordionDetails>
    </MuiAccordion>
  );
}
